<template>
  <div class="teacher-practice-menu navigation-top-app">
    <navigation-top-app
      :app="app"
      :back="() => {
        if ($route.query.student) {
          $router.push({name: 'app.student.profile', params: {code: $route.query.student}, query: {origin: 'app', navigationLeft: 'false', token: app.token}})
        } else {
          $router.push({name: 'app.practice.calendar', query: {origin: 'app', navigationLeft: 'false', token: app.token}})
        }
      }"
    >
      <template v-slot:center>
        <div id="navigation-center">
          {{ app.strings.practice.menu.title }}
        </div>
      </template>
    </navigation-top-app>
    <transition name="fade">
      <div id="teacher-practice-menu-content" v-if="show && !loading">
        <practice-class-card
          class="menu-card"
          :app="app"
          :practice="practice.data"
          :student="practice.student"
          :vehicle="practice.vehicle"
          :type-name="practice.typeName"
          :countdown="countdown"
        />

        <late-end-sheet :app="app" :practice="practice.data" :auto-open="$route.query.late_end === '1'" v-on:done="practiceRead()" />

        <div class="teacher-signature" v-if="teacherSignaturePending">
          <signature-box ref="teacherSignaturePad" :app="app" :label="app.strings.practice.menu.signature_teacher_pending" />
        </div>

        <div class="menu-side">
        <menu-vertical
          class="menu-actions"
          :app="app"
          :color="'blue'"
          :bold="false"
          :padding="{
            top: 0,
            sides: 0
          }"
          :options="menuOptions"
        />

        <incident-sheet :app="app" :practice-id="$route.params.id" v-on:done="practiceRead()" />
        </div>

        <div class="bottom-action" v-if="teacherSignaturePending">
          <button class="h-button w-100" v-on:click="teacherSign()">
            {{ app.strings.practice.menu.signature_teacher_save }}
          </button>
        </div>
        <div class="bottom-action" v-else-if="canPerform && !forgotten">
          <button class="h-button w-100" v-on:click="openAnimated('start')">
            {{ app.strings.practice.menu.start }}
          </button>
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div id="teacher-practice-menu-content" v-if="show && loading">
        <loader-dot :size="'16px'" :color_1="'rgb(32, 113, 183)'" :color_2="'rgb(32, 113, 183)'" />
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'app-teacher-practice-menu-view',
  data () {
    return {
      show: false,
      practice: {
        loading: false,
        stage: 0,
        time: {
          remaining: [-1,0,0,0],
          elapsed: [-1,0,0,0]
        },
        data: {
          started_at: new Date()
        },
        student: {
        },
        vehicle: {},
        typeName: ''
      }
    }
  },
  props: [
    'app'
  ],
  computed: {
    loading () {
      if (this.practice.loading) {
        return true
      }

      return false
    },
    // The class can still be given. Being the main action, it is the button at
    // the bottom of the screen.
    canPerform () {
      return this.practice.data.ended !== 1 && this.practice.data.cancelled !== 1
    },
    // Same rule as late-end-sheet: then the bottom button is «Terminar ahora»
    // and performing the class goes back to the list of options.
    forgotten () {
      let p = this.practice.data
      if (p.started !== 1 || p.ended === 1 || p.cancelled === 1) {
        return false
      }
      let end = new Date(p.date)
      end.setHours(parseInt(p.hour), parseInt(p.minute) + parseInt(p.time) + 60, 0, 0)
      return end < new Date()
    },
    menuOptions () {
      let s = this.app.strings.practice.menu
      let options = []
      if (this.canPerform && this.forgotten) {
        options.push({type: 'button', icon: ['fas', 'clipboard-list'], label: s.start, action: () => this.openAnimated('start')})
      }
      options.push({type: 'button', icon: ['fas', 'user'], label: s.student, action: () => this.openAnimated('student.profile')})
      if (this.practice.data.started !== 1 && this.practice.data.ended !== 1 && this.practice.data.cancelled !== 1) {
        options.push({type: 'button', icon: ['fas', 'pencil-alt'], label: s.edit, action: () => this.openAnimated('edit')})
      }
      return options
    },
    canStart () {
      if (this.practice.data.ended !== 0) {
        return false
      }

      if (this.compareDatesWithoutTime(this.practice.data.date, new Date()) === false) {
        return false
      }

      return true
    },
    teacherSignaturePending () {
      return this.app.user.role === 'teacher' && this.practice.data.ended === 1 && !this.practice.data.teacher_signature
    },
    // The time left, the time running or the real duration, as data.
    countdown () {
      let s = this.app.strings.practice.menu.card
      let c = this.app.strings.common
      let two = (n) => (n < 10 ? '0' : '') + n
      if (this.practice.data.cancelled === 1) {
        return null
      }
      if (this.practice.data.ended === 1) {
        // time_real is what the teacher's app measured; the dates are a
        // fallback (started_at and ended_at were parsed differently before).
        let minutes = parseInt(this.practice.data.time_real) > 0 ? parseInt(this.practice.data.time_real) : Math.round(this.timeReal / 60)
        return {label: s.real_duration, value: isNaN(minutes) ? '' : minutes + ' min'}
      }
      if (this.practice.data.started === 1) {
        // With the days of a class left open: «2 d 05:15:22».
        let e = this.practice.time.elapsed
        return {label: s.running_for, value: e[0] < 0 ? '' : (e[0] > 0 ? e[0] + ' d ' : '') + two(e[1]) + ':' + two(e[2]) + ':' + two(e[3])}
      }
      let r = this.practice.time.remaining
      if (r[0] < 0) {
        return null
      }
      // Its time has come and it has not been started.
      if (r[0] === 0 && r[1] === 0 && r[2] === 0 && r[3] === 0) {
        return {label: s.should_start, value: two(this.practice.data.hour) + ':' + two(this.practice.data.minute)}
      }
      if (r[0] > 0) {
        return {label: s.starts_in, value: r[0] + ' ' + (r[0] === 1 ? c.day : c.days) + ' ' + r[1] + ' ' + (r[1] === 1 ? c.hour : c.hours)}
      }
      return {label: s.starts_in, value: two(r[1]) + ':' + two(r[2]) + ':' + two(r[3])}
    },
    timeReal () {
      return parseInt(this.diffSeconds(new Date(this.practice.data.started_at), new Date(this.practice.data.ended_at)))
    }
  },
  mounted () {
    this.show = true

    this.practice.data.id = this.$route.params.id
    this.practiceRead()

    setInterval(() => {
      this.practice.time.remaining = this.dateFineDiff(this.practice.data.date, 1)
      this.practice.time.elapsed = this.dateFineDiff(this.practice.data.started_at, -1)
    }, 1000)
  },
  methods: {
    practiceRead () {
      if (this.practice.loading) {
        return
      }

      this.practice.loading = true

      this.$http.get(
        this.app.url.api + this.app.endpoints.practice.read.one + this.practice.data.id,
        {
          headers: {
            Authorization: 'Token ' + this.app.token
          }
        }
      ).then(response => {
        if (response.status === 200) {
          if (response.body.success === true) {
            this.practice.data = response.body.data.practice

            this.practice.data.date = new Date(this.practice.data.date)
            this.practice.data.date.setHours(parseInt(this.practice.data.hour), parseInt(this.practice.data.minute), 0, 0)

            this.practice.data.started_at = new Date(this.practice.data.started_at)
            this.practice.data.started_at.setTime(this.practice.data.started_at.getTime() + (this.practice.data.started_at.getTimezoneOffset() * 60 * 1000))

            this.practice.student = response.body.data.student
            this.practice.teacher = response.body.data.teacher
            this.practice.vehicle = response.body.data.vehicle
            this.practice.typeName = (response.body.data.type || {}).name || ''
          } else {
            if (response.body.error.code === 'S100') {
              this.app.methods.logout()
            } else {
              this.showRequestError(this.app.strings, 'practice-read-one', response.body.error)
            }
          }
        } else {
          this.showError(this.app.strings, 'request', 'status-' + response.status)
        }
        this.practice.loading = false
      }, () => {
        this.practice.loading = false
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    },
    teacherSign () {
      const { isEmpty, data } = this.$refs.teacherSignaturePad.saveSignature('image/jpeg')
      if (isEmpty) {
        this.showError(this.app.strings, 'practice-sign', this.app.strings.practice.start.empty.signature_teacher)
        return
      }

      if (this.practice.loading) {
        return
      }

      this.practice.loading = true

      this.$http.post(
        this.app.url.api + this.app.endpoints.practice.sign,
        {
          id: this.practice.data.id,
          signature: data
        },
        {
          emulateJSON: true,
          headers: {
            Authorization: 'Token ' + this.app.token
          }
        }
      ).then(response => {
        this.practice.loading = false
        if (response.status === 200) {
          if (response.body.success === true) {
            this.practiceRead()
          } else {
            if (response.body.error.code === 'S100') {
              this.app.methods.logout()
            } else {
              this.showRequestError(this.app.strings, 'practice-sign', response.body.error)
            }
          }
        } else {
          this.showError(this.app.strings, 'request', 'status-' + response.status)
        }
      }, () => {
        this.practice.loading = false
        this.showError(this.app.strings, 'request', 'status-network: Connection unreachable')
      })
    },
    openAnimated (target) {
      this.show = false
      setTimeout(() => {
        this.open(target)
      }, 500)
    },
    open (target) {
      switch (target) {
        case 'start':
          this.$router.push({name: 'app.teacher.practice.start', params: {id: this.practice.data.id}, query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;
        case 'edit':
          this.$router.push({name: 'app.teacher.practice.create', query: {origin: 'app', navigationLeft: 'false', token: this.app.token, id: this.practice.data.id}})
          break;
        case 'student.profile':
          this.$router.push({name: 'app.student.profile', params: {code: this.practice.data.student}, query: {origin: 'app', navigationLeft: 'false', token: this.app.token}})
          break;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/variables-app.scss";
@import "@/assets/styles/variables-valkiria.scss";

#teacher-practice-menu-content {
  position: relative;
  width: 100%;
  height: 100%;
  padding-top: 60px;
  padding-bottom: 96px;
  box-sizing: border-box;
}

.menu-card {
  margin: 12px 0;
}

.teacher-signature {
  margin-bottom: 12px;
}

@media (min-width: 1024px) {
  // Two columns: the class on the left, the actions on the right.
  #teacher-practice-menu-content {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(320px, 420px);
    column-gap: 28px;
    align-content: start;
    align-items: start;
    padding-top: 84px;
  }

  #teacher-practice-menu-content > * {
    grid-column: 1;
  }

  #teacher-practice-menu-content > .menu-card {
    margin-top: 0;
  }

  #teacher-practice-menu-content > .menu-side {
    grid-column: 2;
    grid-row: 1 / span 4;
  }
}
</style>
